import { Box } from "@material-ui/core";
import { ResponsiveHeatMapCanvas } from "@nivo/heatmap";
import { memo, useCallback, useMemo } from "react";
import { getTextColor, parseLocalDate } from "../../../utils";
import { format } from "date-fns";

const theme = {
  axis: {
    ticks: {
      text: {
        fontSize: 15,
        fontWeight: 600,
      },
    },
    legend: {
      text: {
        fontSize: 15,
        fontWeight: 600,
      },
    },
  },
  labels: {
    text: {
      fontSize: 15,
      fontWeight: 600,
    },
  },
};

// Calculate evenly spaced tick values to avoid overcrowding
function getAdaptiveTickValues(values, maxTicks = 20) {
  if (!values || values.length <= maxTicks) return undefined; // show all
  const step = Math.ceil(values.length / maxTicks);
  return values.filter((_, i) => i % step === 0);
}

const tooltipStyles = {
  backgroundColor: "#fff",
  border: "1px solid #999",
  borderRadius: 6,
  padding: 10,
  fontSize: 14,
  lineHeight: 1.5,
  color: "#333",
};

function DepthHeatmapChart({
  matrix,
  dates,
  depthLabel = "Depth (m)",
  colorScale,
  breaks,
  colors,
  height = 400,
  unit = "units",
  showValues = true,
  onClick = null,
  xAxisFormat = "date", // "date" or "time"
}) {
  const getCellColor = useCallback(
    (cell) => (cell.value != null ? colorScale(cell.value) : "#eee"),
    [colorScale]
  );

  const getLabel = useCallback((cell) => cell.value?.toFixed(1) ?? "", []);

  const getLabelTextColor = useCallback(
    (cell) =>
      getTextColor(cell.value != null ? colorScale(cell.value) : "#eee"),
    [colorScale]
  );

  // For time format (15-min data), we have ~96 points per day, show ~24 (hourly)
  // For date format (daily data), limit to ~20-25 ticks max
  const maxTicks = xAxisFormat === "time" ? 24 : 25;
  const tickValues = useMemo(
    () => getAdaptiveTickValues(dates, maxTicks),
    [dates, maxTicks]
  );

  const axisTop = useMemo(
    () => ({
      tickRotation: -90,
      tickPadding: 8,
      legendOffset: 30,
      tickValues,
      format: (val) => {
        try {
          if (xAxisFormat === "time") {
            return format(new Date(val), "HH:mm");
          }
          return format(parseLocalDate(val), "MM/dd/yyyy");
        } catch {
          return val;
        }
      },
      tickSize: 8,
    }),
    [xAxisFormat, tickValues]
  );

  const axisLeft = useMemo(
    () => ({
      legend: depthLabel,
      legendOffset: -45,
      legendPosition: "middle",
      tickSize: 8,
    }),
    [depthLabel]
  );

  const renderTooltip = useCallback(
    ({ cell }) => {
      const dateVal = cell?.data?.x;
      const value = cell?.value;
      const depth = cell?.serieId;

      let formattedDate;
      try {
        if (xAxisFormat === "time") {
          formattedDate = format(new Date(dateVal), "MM/dd/yyyy HH:mm");
        } else {
          formattedDate = format(parseLocalDate(dateVal), "MM/dd/yyyy");
        }
      } catch {
        formattedDate = dateVal;
      }

      return (
        <Box style={tooltipStyles}>
          <div>
            <strong>{formattedDate}</strong>
          </div>
          <div>{depth} meters</div>
          <div>
            {value?.toFixed?.(2) ?? "--"} {unit}
          </div>
        </Box>
      );
    },
    [unit, xAxisFormat]
  );

  const handleClick = useCallback(
    (cell) => {
      if (onClick && cell?.data?.x) {
        onClick(cell.data.x);
      }
    },
    [onClick]
  );

  return (
    <Box style={{ display: "flex", flexDirection: "column" }}>
      <Box style={{ height, cursor: onClick ? "pointer" : "default" }}>
        <ResponsiveHeatMapCanvas
          data={matrix}
          animate={false}
          margin={{ top: 100, right: 0, bottom: 0, left: 60 }}
          theme={theme}
          colors={getCellColor}
          emptyColor="#eee"
          axisTop={axisTop}
          axisLeft={axisLeft}
          tooltip={renderTooltip}
          onClick={onClick ? handleClick : undefined}
          enableLabels={showValues}
          label={getLabel}
          labelTextColor={getLabelTextColor}
          hoverTarget="rowColumn"
          pixelRatio={2}
        />
      </Box>

      {/* legend */}
      {breaks.length > 0 && (
        <Box pt={2} display="flex" justifyContent="center">
          {breaks.map((b, i) => (
            <Box key={i} textAlign="center" mx={2}>
              <div
                style={{
                  background: colors[i],
                  width: 40,
                  height: 20,
                  border: "1px solid #ccc",
                }}
              />
              <div style={{ fontSize: "1rem", fontWeight: 600 }}>
                {Number.isInteger(b) ? b : b.toFixed(1)}
              </div>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

export default memo(DepthHeatmapChart);
